import React, { useState, useEffect, useCallback } from 'react';
import {
    Plus,
    Trash2,
    X,
    Check,
    Compass,
    Zap,
    Book,
    Edit3,
    ShoppingBag,
    Settings,
    ChevronRight,
    Play,
    Pause,
    Layout,
    AlertCircle
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { auth } from '../../../../../firebase/config/config';

// Platforms Configuration
const PLATFORM_PRESETS = {
    moodle: {
        name: 'Moodle LMS',
        icon: Book,
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        actions: [
            {
                id: 'logout',
                name: 'Help with Logout',
                description: 'Guide users to find and click the logout button',
                triggerPhrases: ["can't logout", "unable to logout", "how to logout", "log out", "sign out", "can't sign out"],
                steps: [
                    {
                        selector: '.usermenu .dropdown-toggle, #user-menu-toggle, .userbutton',
                        action: 'highlight',
                        message: 'First, click on your profile menu here 👆',
                        tooltipPosition: 'bottom'
                    },
                    {
                        selector: '.dropdown-menu a[href*="logout"], a.logout, [data-action="logout"]',
                        action: 'tooltip',
                        message: 'Now click "Log out" to sign out of your account',
                        tooltipPosition: 'left'
                    }
                ]
            },
            {
                id: 'dashboard',
                name: 'Navigate to Dashboard',
                description: 'Show users how to access their dashboard',
                triggerPhrases: ['find dashboard', 'where is dashboard', 'go to dashboard', 'my courses'],
                steps: [
                    {
                        selector: 'a[href*="my"], .nav-link[href*="my"], #nav-drawer a[data-key="myhome"]',
                        action: 'highlight',
                        message: 'Click here to go to your Dashboard 📊',
                        tooltipPosition: 'right'
                    }
                ]
            },
            {
                id: 'profile',
                name: 'Edit Profile',
                description: 'Guide users to their profile settings',
                triggerPhrases: ['edit profile', 'change profile', 'update profile', 'profile settings'],
                steps: [
                    {
                        selector: '.usermenu .dropdown-toggle, #user-menu-toggle, .userbutton',
                        action: 'highlight',
                        message: 'Click on your profile menu 👆',
                        tooltipPosition: 'bottom'
                    },
                    {
                        selector: 'a[href*="profile"], .dropdown-menu a[href*="user/profile"]',
                        action: 'tooltip',
                        message: 'Select "Profile" to view and edit your information',
                        tooltipPosition: 'left'
                    }
                ]
            }
        ]
    },
    wordpress: {
        name: 'WordPress',
        icon: Edit3,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        actions: [
            {
                id: 'logout',
                name: 'Help with Logout',
                description: 'Guide users to find the logout option',
                triggerPhrases: ["can't logout", "how to logout", "log out", "sign out"],
                steps: [
                    {
                        selector: '#wp-admin-bar-my-account, .woocommerce-MyAccount-navigation-link--customer-logout a, a[href*="logout"]',
                        action: 'highlight',
                        message: 'Click here to access your account menu, then select "Log Out" 👆',
                        tooltipPosition: 'bottom'
                    }
                ]
            },
            {
                id: 'cart',
                name: 'Find Shopping Cart',
                description: 'Help users locate their shopping cart',
                triggerPhrases: ['where is cart', 'find cart', 'shopping cart', 'view cart', 'my cart'],
                steps: [
                    {
                        selector: '.cart-icon, .woocommerce-mini-cart, a[href*="cart"], .cart-contents',
                        action: 'highlight',
                        message: 'Your shopping cart is here! Click to view items 🛒',
                        tooltipPosition: 'bottom'
                    }
                ]
            }
        ]
    },
    shopify: {
        name: 'Shopify Store',
        icon: ShoppingBag,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        actions: [
            {
                id: 'account',
                name: 'Access Account',
                description: 'Help users find their account menu',
                triggerPhrases: ['my account', 'login to account', 'account settings', 'sign in'],
                steps: [
                    {
                        selector: '.site-header__account, a[href*="/account"], .customer-account-link',
                        action: 'highlight',
                        message: 'Click here to access your account 👤',
                        tooltipPosition: 'bottom'
                    }
                ]
            },
            {
                id: 'logout',
                name: 'Help with Logout',
                description: 'Guide users to logout',
                triggerPhrases: ["can't logout", "how to logout", "log out"],
                steps: [
                    {
                        selector: 'a[href*="logout"], .customer-logout, button[name="logout"]',
                        action: 'highlight',
                        message: 'Click "Log out" here to sign out of your account',
                        tooltipPosition: 'bottom'
                    }
                ]
            }
        ]
    },
    generic: {
        name: 'Custom / Other',
        icon: Settings,
        color: 'text-slate-600',
        bgColor: 'bg-slate-50',
        actions: [
            {
                id: 'custom',
                name: 'Custom Walkthrough',
                description: 'Create your own walkthrough with custom selectors',
                triggerPhrases: [],
                steps: []
            }
        ]
    }
};

function Walkthroughs({ selectedChatbotId }) {
    const [walkthroughs, setWalkthroughs] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // Form state
    const [selectedPlatform, setSelectedPlatform] = useState(null);
    const [selectedActions, setSelectedActions] = useState([]);
    const [customName, setCustomName] = useState('');
    const [customTriggers, setCustomTriggers] = useState([]);
    const [triggerInput, setTriggerInput] = useState('');

    // Fetch walkthroughs
    const fetchWalkthroughs = useCallback(async () => {
        if (!selectedChatbotId) return;

        try {
            const response = await fetch(`/api/chatbot?chatbotId=${selectedChatbotId}`);
            if (response.ok) {
                const data = await response.json();
                setWalkthroughs(data.config?.walkthroughs || []);
            }
        } catch (error) {
            console.error('Error fetching walkthroughs:', error);
        } finally {
            setIsLoading(false);
        }
    }, [selectedChatbotId]);

    useEffect(() => {
        fetchWalkthroughs();
    }, [fetchWalkthroughs]);

    // Save walkthroughs
    const saveWalkthroughs = async (newWalkthroughs) => {
        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            const headers = { 'Content-Type': 'application/json' };
            if (token) headers.Authorization = `Bearer ${token}`;

            const response = await fetch(`/api/chatbot?chatbotId=${selectedChatbotId}`);
            if (!response.ok) throw new Error('Failed to fetch config');

            const data = await response.json();
            const currentConfig = data.config || {};

            const updatedConfig = { ...currentConfig, walkthroughs: newWalkthroughs };

            const saveResponse = await fetch('/api/chatbot', {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    chatbotId: selectedChatbotId,
                    config: updatedConfig,
                    userId: user?.uid || currentConfig.userId
                })
            });

            if (!saveResponse.ok) throw new Error('Failed to save');

            setWalkthroughs(newWalkthroughs);
            return true;
        } catch (error) {
            console.error('Error saving walkthroughs:', error);
            return false;
        }
    };

    const handleAddNew = () => {
        setSelectedPlatform(null);
        setSelectedActions([]);
        setCustomName('');
        setCustomTriggers([]);
        setIsModalOpen(true);
    };

    const handleToggle = async (id) => {
        const updated = walkthroughs.map(w =>
            w.id === id ? { ...w, isEnabled: !w.isEnabled } : w
        );
        await saveWalkthroughs(updated);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this walkthrough?')) return;
        const updated = walkthroughs.filter(w => w.id !== id);
        await saveWalkthroughs(updated);
    };

    const toggleAction = (action) => {
        setSelectedActions(prev => {
            const exists = prev.find(a => a.id === action.id);
            if (exists) return prev.filter(a => a.id !== action.id);
            return [...prev, action];
        });
    };

    const handleAddTrigger = (e) => {
        if (e.key === 'Enter' && triggerInput.trim()) {
            e.preventDefault();
            if (!customTriggers.includes(triggerInput.trim())) {
                setCustomTriggers([...customTriggers, triggerInput.trim()]);
            }
            setTriggerInput('');
        }
    };

    const removeTrigger = (trigger) => {
        setCustomTriggers(customTriggers.filter(t => t !== trigger));
    };

    const handleSave = async () => {
        if (!selectedPlatform || (selectedPlatform !== 'generic' && selectedActions.length === 0)) return;

        // Manual validation for generic
        if (selectedPlatform === 'generic' && !customName.trim()) return;

        const newWalkthroughs = selectedActions.length > 0
            ? selectedActions.map(action => ({
                id: uuidv4(),
                name: action.name,
                platform: selectedPlatform,
                triggerPhrases: action.id === 'custom' ? customTriggers : action.triggerPhrases,
                steps: action.steps,
                isEnabled: true,
                createdAt: new Date().toISOString()
            }))
            : [{ // Generic case
                id: uuidv4(),
                name: customName,
                platform: selectedPlatform,
                triggerPhrases: customTriggers,
                steps: [], // Would need step builder logic in future
                isEnabled: true,
                createdAt: new Date().toISOString()
            }];

        const updated = [...walkthroughs, ...newWalkthroughs];
        const success = await saveWalkthroughs(updated);

        if (success) {
            setIsModalOpen(false);
            setSelectedPlatform(null);
            setSelectedActions([]);
        }
    };

    if (isLoading) {
        return (
            <div className="flex h-full w-full bg-slate-50/50 p-3 items-center justify-center">
                <p className="text-slate-400 text-sm font-medium animate-pulse">Loading walkthroughs...</p>
            </div>
        );
    }

    return (
        <div className="flex h-full w-full bg-slate-50/50 p-3 gap-3 overflow-hidden">
            <div className="w-full h-full flex flex-col bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
                {/* Header */}
                <div className="p-4 border-b border-slate-100 bg-white sticky top-0 z-10 flex justify-between items-center">
                    <div>
                        <h2 className="text-lg font-bold tracking-tight text-slate-900">Guided Walkthroughs <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 ml-2">Beta</span></h2>
                        <p className="text-xs text-slate-500 font-medium mt-0.5">Automated on-screen guidance triggered by user questions.</p>
                    </div>
                    <button
                        onClick={handleAddNew}
                        className="flex items-center gap-2 px-3 py-1.5 bg-slate-900 text-white text-xs font-semibold rounded-lg hover:bg-slate-800 transition-colors shadow-sm"
                    >
                        <Plus size={14} />
                        Add New
                    </button>
                </div>

                {/* Beta Banner */}
                <div className="mx-6 mt-6 p-4 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 border border-blue-100 rounded-xl flex items-center justify-between gap-5 relative overflow-hidden">
                    {/* Decorative Background Element */}
                    <div className="absolute -right-6 -top-6 w-24 h-24 bg-blue-100/50 rounded-full blur-2xl pointer-events-none"></div>

                    <div className="flex items-center gap-4 relative z-10">
                        <div className="w-10 h-10 flex items-center justify-center bg-white text-blue-600 rounded-lg shadow-sm border border-blue-50 shrink-0">
                            <Zap size={20} className="fill-blue-600/20" />
                        </div>
                        <div>
                            <h4 className="text-sm font-bold text-slate-900">We're building the future of automated guidance!</h4>
                            <p className="text-xs text-slate-600 mt-1 max-w-xl leading-relaxed">
                                The Walkthroughs feature is currently in Beta. We are working on adding more platforms and advanced custom selectors.
                                Have a specific use case? Let us build a tailored walkthrough for your site.
                            </p>
                        </div>
                    </div>
                    <a
                        href="mailto:support@smushlabs.ai?subject=Tailored Walkthrough Request"
                        className="shrink-0 px-4 py-2 bg-white border border-blue-200 text-blue-700 text-xs font-bold rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-all shadow-sm whitespace-nowrap relative z-10"
                    >
                        Contact Us
                    </a>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    <div className="max-w-4xl mx-auto px-6 py-6 pt-2">
                        {walkthroughs.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-16 text-center">
                                <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mb-4 text-slate-300">
                                    <Compass size={32} />
                                </div>
                                <h3 className="text-sm font-bold text-slate-900 mb-1">No walkthroughs yet</h3>
                                <p className="text-xs text-slate-500 max-w-xs mx-auto leading-relaxed mb-6">
                                    Create guided tours that activate when users ask specific questions like "How do I logout?"
                                </p>
                                <button
                                    onClick={handleAddNew}
                                    className="px-4 py-2 bg-white border border-dashed border-slate-300 text-slate-600 text-xs font-semibold rounded-lg hover:border-slate-400 hover:text-slate-900 transition-all"
                                >
                                    Create First Walkthrough
                                </button>
                            </div>
                        ) : (
                            <div className="grid gap-3">
                                {walkthroughs.map(walkthrough => {
                                    const PlatformIcon = PLATFORM_PRESETS[walkthrough.platform]?.icon || Settings;
                                    const platformColor = PLATFORM_PRESETS[walkthrough.platform]?.color || 'text-slate-600';
                                    const platformBg = PLATFORM_PRESETS[walkthrough.platform]?.bgColor || 'bg-slate-50';

                                    return (
                                        <div key={walkthrough.id} className="group flex items-start gap-4 p-4 rounded-xl border border-slate-100 hover:border-slate-200 bg-white shadow-sm hover:shadow-md transition-all">
                                            {/* Icon */}
                                            <div className={`w-10 h-10 rounded-lg ${platformBg} ${platformColor} flex items-center justify-center shrink-0`}>
                                                <PlatformIcon size={20} />
                                            </div>

                                            {/* Info */}
                                            <div className="flex-1 min-w-0 pt-0.5">
                                                <div className="flex items-start justify-between gap-4">
                                                    <div>
                                                        <h4 className="text-sm font-bold text-slate-900 truncate">
                                                            {walkthrough.name}
                                                        </h4>
                                                        <div className="flex items-center gap-2 mt-1">
                                                            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-500 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100">
                                                                <Layout size={10} />
                                                                {PLATFORM_PRESETS[walkthrough.platform]?.name || 'Custom'}
                                                            </span>
                                                            <span className="text-[10px] text-slate-400">•</span>
                                                            <span className="text-[10px] text-slate-500">
                                                                {walkthrough.steps?.length || 0} step{walkthrough.steps?.length !== 1 ? 's' : ''}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Actions */}
                                                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <button
                                                            onClick={() => handleToggle(walkthrough.id)}
                                                            title={walkthrough.isEnabled ? "Disable" : "Enable"}
                                                            className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors ${walkthrough.isEnabled ? 'text-green-600 bg-green-50 hover:bg-green-100' : 'text-slate-400 bg-slate-50 hover:bg-slate-100'}`}
                                                        >
                                                            {walkthrough.isEnabled ? <Pause size={14} /> : <Play size={14} />}
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(walkthrough.id)}
                                                            className="w-7 h-7 flex items-center justify-center rounded-lg text-red-400 bg-red-50 hover:bg-red-100 hover:text-red-600 transition-colors"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Triggers */}
                                                {(walkthrough.triggerPhrases || []).length > 0 && (
                                                    <div className="mt-3 flex flex-wrap gap-1.5">
                                                        {walkthrough.triggerPhrases.slice(0, 4).map((phrase, i) => (
                                                            <span key={i} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-50 text-slate-500 border border-slate-100">
                                                                "{phrase}"
                                                            </span>
                                                        ))}
                                                        {walkthrough.triggerPhrases.length > 4 && (
                                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-50 text-slate-400">
                                                                +{walkthrough.triggerPhrases.length - 4} more
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* Add/Edit Modal */}
                {isModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/20 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                        <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-200">

                            {/* Modal Header */}
                            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-white sticky top-0 z-10">
                                <div>
                                    <h3 className="text-sm font-bold text-slate-900">Add Walkthrough</h3>
                                    <p className="text-xs text-slate-500 mt-0.5">Configure new guidance for your users.</p>
                                </div>
                                <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                                    <X size={18} />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div className="flex-1 overflow-y-auto p-5 custom-scrollbar">
                                {/* Step 1: Platform Selection */}
                                {!selectedPlatform && (
                                    <div className="space-y-4">
                                        <div className="text-center pb-2">
                                            <h4 className="text-sm font-semibold text-slate-900">Choose Platform</h4>
                                            <p className="text-xs text-slate-500">Select your website platform for pre-made templates</p>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            {Object.entries(PLATFORM_PRESETS).map(([key, platform]) => {
                                                const Icon = platform.icon;
                                                return (
                                                    <button
                                                        key={key}
                                                        onClick={() => setSelectedPlatform(key)}
                                                        className="flex flex-col items-center justify-center gap-3 p-4 rounded-xl border border-slate-200 hover:border-slate-800 hover:bg-slate-50 transition-all group text-center h-28"
                                                    >
                                                        <div className={`p-2 rounded-lg ${platform.bgColor} ${platform.color} group-hover:scale-110 transition-transform`}>
                                                            <Icon size={24} />
                                                        </div>
                                                        <span className="text-xs font-semibold text-slate-700 group-hover:text-slate-900">{platform.name}</span>
                                                    </button>
                                                )
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Step 2: Configuration */}
                                {selectedPlatform && (
                                    <div className="space-y-6">
                                        {/* Back Button */}
                                        <button
                                            onClick={() => { setSelectedPlatform(null); setSelectedActions([]); }}
                                            className="flex items-center gap-1 text-xs font-medium text-slate-400 hover:text-slate-600 transition-colors"
                                        >
                                            <ChevronRight size={12} className="rotate-180" />
                                            Back to Platforms
                                        </button>

                                        {/* Platform Header */}
                                        <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                                            <div className={`p-2 rounded-lg ${PLATFORM_PRESETS[selectedPlatform].bgColor} ${PLATFORM_PRESETS[selectedPlatform].color}`}>
                                                {React.createElement(PLATFORM_PRESETS[selectedPlatform].icon, { size: 20 })}
                                            </div>
                                            <div>
                                                <h4 className="text-sm font-bold text-slate-900">{PLATFORM_PRESETS[selectedPlatform].name}</h4>
                                                <p className="text-xs text-slate-500">Select the actions you want to enable</p>
                                            </div>
                                        </div>

                                        {/* Preset Actions */}
                                        {selectedPlatform !== 'generic' && (
                                            <div className="space-y-3">
                                                {PLATFORM_PRESETS[selectedPlatform].actions.map(action => {
                                                    const isSelected = selectedActions.find(a => a.id === action.id);
                                                    return (
                                                        <div
                                                            key={action.id}
                                                            onClick={() => toggleAction(action)}
                                                            className={`cursor-pointer p-3 rounded-xl border transition-all flex items-start gap-3 ${isSelected ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900/5' : 'border-slate-200 hover:border-slate-300 hover:bg-white'}`}
                                                        >
                                                            <div className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center transition-colors ${isSelected ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 bg-white'}`}>
                                                                {isSelected && <Check size={10} strokeWidth={3} />}
                                                            </div>
                                                            <div>
                                                                <h5 className="text-xs font-bold text-slate-900">{action.name}</h5>
                                                                <p className="text-[11px] text-slate-500 mt-0.5">{action.description}</p>
                                                                {/* Mini pill showing trigger count */}
                                                                <div className="mt-2 inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 text-[10px] text-slate-500 font-medium">
                                                                    <Zap size={10} className="mr-1" />
                                                                    {action.triggerPhrases.length} triggers
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Generic / Custom Form */}
                                        {selectedPlatform === 'generic' && (
                                            <div className="space-y-5">
                                                {/* Warning */}
                                                <div className="p-3 bg-amber-50 text-amber-800 rounded-lg border border-amber-100 flex gap-3 items-start">
                                                    <AlertCircle size={16} className="shrink-0 mt-0.5" />
                                                    <div className="text-xs">
                                                        <strong className="block mb-1">Advanced Setup</strong>
                                                        Custom walkthroughs require knowledge of CSS Selectors. Use browser dev tools to find element selectors.
                                                    </div>
                                                </div>

                                                <div className="space-y-4">
                                                    <div>
                                                        <label className="block text-xs font-semibold text-slate-700 mb-1.5">Walkthrough Name</label>
                                                        <input
                                                            type="text"
                                                            value={customName}
                                                            onChange={e => setCustomName(e.target.value)}
                                                            placeholder="e.g. Help with Navigation"
                                                            className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-slate-900 focus:ring-0 transition-all placeholder:text-slate-400"
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block text-xs font-semibold text-slate-700 mb-1.5">Trigger Phrases</label>
                                                        <div className="min-h-[80px] p-2 bg-white border border-slate-200 rounded-lg focus-within:border-slate-900 focus-within:ring-0 transition-all">
                                                            <div className="flex flex-wrap gap-1.5 mb-2">
                                                                {customTriggers.map((t, i) => (
                                                                    <span key={i} className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 text-xs font-medium text-slate-700">
                                                                        {t}
                                                                        <button onClick={() => removeTrigger(t)} className="ml-1 text-slate-400 hover:text-slate-600"><X size={10} /></button>
                                                                    </span>
                                                                ))}
                                                            </div>
                                                            <input
                                                                type="text"
                                                                value={triggerInput}
                                                                onChange={e => setTriggerInput(e.target.value)}
                                                                onKeyDown={handleAddTrigger}
                                                                placeholder={customTriggers.length === 0 ? "Type phrase & press Enter..." : "Add another..."}
                                                                className="w-full text-sm bg-transparent border-none focus:ring-0 p-0 placeholder:text-slate-400"
                                                            />
                                                        </div>
                                                        <p className="text-[10px] text-slate-400 mt-1.5">Type a phrase like "reset password" and press Enter to add.</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={!selectedPlatform || (selectedPlatform !== 'generic' && selectedActions.length === 0)}
                                    className="px-4 py-2 bg-slate-900 text-white text-xs font-semibold rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                                >
                                    {selectedPlatform === 'generic' ? 'Create Custom Walkthrough' : 'Add Selected Walkthroughs'}
                                </button>
                            </div>

                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Walkthroughs;
